import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { staggerContainer, staggerItem } from '../../lib/animations';
import { getApiUrl } from '../../lib/api';
import type { CountryProps } from '../../pages/Cannabis';

/* ── Types ── */
interface CountryLaw {
  id?: number;
  country: string;
  country_code?: string;
  continent?: string;
  legal_status?: string;
  possession_limit?: string;
  penalties?: string;
  medical_program?: string;
  recreational?: string;
  notes?: string;
  last_updated?: string;
}

import { countryFlag, statusColor } from '../../lib/country-utils';

/* ── Helpers ── */
const statusBadge = statusColor;

function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`animate-pulse bg-white/5 rounded-xl ${className}`} />;
}

const STATUS_FILTERS = ['All', 'Legal', 'Medical', 'Decriminalized', 'Illegal'] as const;
const CONTINENTS = ['All', 'Africa', 'Americas', 'Asia', 'Europe', 'Oceania'] as const;

export default function LawsTab({ userCountry, isGlobal }: CountryProps) {
  const [laws, setLaws] = useState<CountryLaw[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [continentFilter, setContinentFilter] = useState('All');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    async function fetchData() {
      try {
        const res = await fetch(getApiUrl('/api/country-laws'));
        if (!res.ok) throw new Error('Failed to fetch');
        const data = await res.json();
        setLaws(Array.isArray(data) ? data : data.data ?? []);
      } catch {
        setLaws([]);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const filtered = useMemo(() => {
    const result = laws.filter(l => {
      if (search.trim() && !(typeof l.country === 'string' && l.country.toLowerCase().includes(search.toLowerCase()))) return false;
      if (statusFilter !== 'All') {
        const s = typeof l.legal_status === 'string' ? l.legal_status.toLowerCase() : '';
        const f = typeof statusFilter === 'string' ? statusFilter.toLowerCase() : '';
        if (f === 'legal' && (!s.includes('legal') || s.includes('illegal'))) return false;
        if (f === 'illegal' && !s.includes('illegal')) return false;
        if (f === 'medical' && !s.includes('medical')) return false;
        if (f === 'decriminalized' && !s.includes('decriminal')) return false;
      }
      if (continentFilter !== 'All' && !(typeof l.continent === 'string' && typeof continentFilter === 'string' && l.continent.toLowerCase() === continentFilter.toLowerCase())) return false;
      return true;
    });

    // Pin user's country to the top when not in global mode
    if (userCountry && !isGlobal) {
      const idx = result.findIndex(l => l.country === userCountry.name);
      if (idx > 0) {
        const [item] = result.splice(idx, 1);
        result.unshift(item);
      }
    }

    return result;
  }, [laws, search, statusFilter, continentFilter, userCountry, isGlobal]);

  const lawKey = (l: CountryLaw, i: number) => l.id ? String(l.id) : `${l.country}-${i}`;

  // Pre-expand user's country on initial load
  useEffect(() => {
    if (userCountry && !isGlobal && filtered.length > 0) {
      const userLaw = filtered.find(l => l.country === userCountry.name);
      if (userLaw) {
        const idx = filtered.indexOf(userLaw);
        setExpandedId(lawKey(userLaw, idx));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 w-full" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-40" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search */}
      <input
        type="text"
        placeholder="Search countries..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#4ade80]/40 transition-colors"
      />

      {/* Status Filter */}
      <div className="flex gap-2 flex-wrap">
        {STATUS_FILTERS.map(s => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-xl border transition-colors ${
              statusFilter === s
                ? 'bg-[#4ade80]/20 border-[#4ade80]/40 text-[#4ade80]'
                : 'bg-white/5 border-white/10 text-white/50 hover:border-white/20'
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {/* Continent Tabs */}
      <div className="flex gap-2 flex-wrap">
        {CONTINENTS.map(c => (
          <button
            key={c}
            onClick={() => setContinentFilter(c)}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
              continentFilter === c
                ? 'bg-[#d4a574]/20 text-[#d4a574] border border-[#d4a574]/30'
                : 'text-white/40 hover:text-white/60 border border-transparent'
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Results */}
      <p className="text-white/40 text-sm">{filtered.length} countries</p>

      {filtered.length === 0 ? (
        <div className="text-center py-16 text-white/40">
          <p>No countries match your filters.</p>
        </div>
      ) : (
        <motion.div
          variants={staggerContainer}
          initial="initial"
          animate="animate"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
        >
          {filtered.map((l, i) => {
            const key = lawKey(l, i);
            const isExpanded = expandedId === key;
            const flag = countryFlag(l.country_code);

            return (
              <motion.div key={key} variants={staggerItem} layout>
                <div
                  onClick={() => setExpandedId(isExpanded ? null : key)}
                  className={`cursor-pointer rounded-2xl border p-4 transition-colors ${
                    isExpanded
                      ? 'border-[#4ade80]/40 bg-white/[0.04]'
                      : 'border-white/10 bg-white/[0.02] hover:border-white/20'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {flag && <span className="text-2xl">{flag}</span>}
                    <div className="flex-1 min-w-0 space-y-2">
                      <h3 className="text-white font-semibold text-sm">
                      {l.country}
                      {userCountry && !isGlobal && l.country === userCountry.name && (
                        <span className="ml-2 px-1.5 py-0.5 text-[9px] font-semibold rounded-full bg-[#4ade80]/20 text-[#4ade80] border border-[#4ade80]/30">Your Country</span>
                      )}
                    </h3>
                      {l.legal_status && (
                        <span className={`inline-block px-2 py-0.5 text-[10px] font-semibold rounded-full border ${statusBadge(l.legal_status)}`}>
                          {l.legal_status}
                        </span>
                      )}
                      {l.possession_limit && (
                        <p className="text-white/50 text-xs">
                          Possession: {l.possession_limit}
                        </p>
                      )}
                      {l.penalties && (
                        <p className="text-white/40 text-xs line-clamp-2">
                          {l.penalties}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Expanded Detail */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="mt-4 pt-4 border-t border-white/10 space-y-3">
                          {l.medical_program && (
                            <div>
                              <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1">Medical Program</p>
                              <p className="text-white/70 text-xs">{l.medical_program}</p>
                            </div>
                          )}
                          {l.recreational && (
                            <div>
                              <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1">Recreational</p>
                              <p className="text-white/70 text-xs">{l.recreational}</p>
                            </div>
                          )}
                          {l.penalties && (
                            <div>
                              <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1">Penalties</p>
                              <p className="text-white/70 text-xs leading-relaxed">{l.penalties}</p>
                            </div>
                          )}
                          {l.notes && (
                            <div>
                              <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1">Notes</p>
                              <p className="text-white/70 text-xs">{l.notes}</p>
                            </div>
                          )}
                          {l.continent && (
                            <p className="text-white/30 text-xs">Continent: {l.continent}</p>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      )}
    </div>
  );
}
